<script setup lang="ts">
import { computed, toRef } from "vue";
import { Select } from "@/components/episto-ui/select";
import OptionRow from "./OptionRow.vue";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import { useChoiceColors } from "@/composables/useChoiceColors";

const props = defineProps({
  card: { type: Object, required: true },
  cardMeta: { type: Object, default: null },
  displayLocale: { type: String, default: null },
  npsChartType: { type: Boolean, default: false },
});

const cardRef = toRef(props, "card");
const { localOptions, updateLocalOption } = useCardLocalOptions(cardRef);

const {
  choiceCodes,
  choiceLabelsMap,
  hasChoiceColors,
  choiceColors,
  paletteOptions,
  selectedPalette,
  defaultChoiceColors,
  defaultPopColor,
  updateSingleChoiceColor,
} = useChoiceColors(
  toRef(props, "cardMeta"),
  localOptions,
  updateLocalOption,
  toRef(props, "displayLocale"),
  toRef(props, "npsChartType"),
);

const POPULATION = "population";
const CUSTOM = "custom";

const colorOptions = computed(() => [
  { value: POPULATION, label: "Population color" },
  ...(props.npsChartType ? [] : paletteOptions.filter((o) => o.value).map((o) => ({ value: String(o.value), label: o.label }))),
  { value: CUSTOM, label: "Custom colors" },
]);

const colorMode = computed(() => {
  if (!hasChoiceColors.value) return POPULATION;
  if (props.npsChartType) return CUSTOM;
  return selectedPalette.value || CUSTOM;
});

function setColorMode(mode: unknown) {
  if (mode === POPULATION) hasChoiceColors.value = false;
  else if (mode === CUSTOM) {
    updateLocalOption("choice_color_palette", null);
    if (!hasChoiceColors.value) hasChoiceColors.value = true;
  } else selectedPalette.value = String(mode);
}
</script>

<template>
  <OptionRow
    label="Colors"
    hint="One color per answer. When comparing populations, colors follow the populations instead.">
    <Select
      :model-value="colorMode"
      :options="colorOptions"
      @update:model-value="setColorMode" />
    <div
      v-if="hasChoiceColors"
      class="mt-2 space-y-1">
      <div
        v-for="code in choiceCodes"
        :key="code"
        class="flex items-center gap-2">
        <label
          class="relative size-5 shrink-0 cursor-pointer overflow-hidden rounded border border-border"
          :style="{ backgroundColor: choiceColors[code] || defaultChoiceColors[code] || defaultPopColor }"
          :aria-label="'Pick color for ' + (choiceLabelsMap[code] || code)">
          <input
            type="color"
            :value="choiceColors[code] || defaultChoiceColors[code] || defaultPopColor"
            class="sr-only"
            @input="updateSingleChoiceColor(code, ($event.target as HTMLInputElement).value)" />
        </label>
        <span class="truncate text-xs text-muted-foreground">{{ choiceLabelsMap[code] || code }}</span>
      </div>
    </div>
  </OptionRow>
</template>
